import { Chip } from "@heroui/react";
import { useMemo, useState } from "react";

import type { DataGridColumn } from "@/components/kit";

import {
  AttributionFilterSelects,
  SourceCell,
  appliedAttributionFilters,
} from "@/components/attribution";
import {
  AppliedFilters,
  DataTable,
  FiltersPopover,
  SearchBox,
} from "@/components/kit";
import { PersonCell, Toolbar, formatDayTime } from "@/components/sales/shared";
import { useAcquisitionOptions } from "@/hooks/api/users/useAcquisitionOptions";
import { AttributionFilters, User } from "@/types";

export interface UserTableProps {
  items: User[];
  page: number;
  totalUsers: number;
  changePage: (page: number) => void;
  isLoading: boolean;
  searchTerm: string;
  handleSearchTermChange: (searchTerm: string) => void;
  limit?: number;
  onLimitChange?: (limit: number) => void;
  filters: AttributionFilters;
  onFiltersChange: (filters: AttributionFilters) => void;
}

const columns: DataGridColumn<User>[] = [
  {
    id: "name",
    header: "User",
    isRowHeader: true,
    minWidth: 260,
    cell: (user) => (
      <PersonCell
        firstName={user.firstName}
        lastName={user.lastName}
        phoneNumber={user.phoneNumber}
      />
    ),
  },
  {
    id: "email",
    header: "Email",
    cell: (user) =>
      user.email ? (
        <a
          className="decoration-separator underline underline-offset-4 hover:decoration-current"
          href={`mailto:${user.email}`}
        >
          {user.email}
        </a>
      ) : (
        <span className="text-muted">Not set</span>
      ),
  },
  {
    id: "firstOrderId",
    header: "Has ordered",
    cell: (user) => (
      <Chip
        color={user.firstOrderId ? "success" : "default"}
        variant="soft"
      >
        {user.firstOrderId ? "Yes" : "No"}
      </Chip>
    ),
  },
  {
    id: "source",
    header: "Source",
    minWidth: 140,
    cell: (user) => <SourceCell attribution={user.acquisition} />,
  },
  {
    id: "createdAt",
    header: "Created",
    cell: (user) => (
      <span className="tabular-nums">
        {formatDayTime(user.createdAt)}
      </span>
    ),
  },
];

export const UserListTable = ({
  searchTerm,
  handleSearchTermChange,
  items,
  page,
  totalUsers,
  changePage,
  isLoading,
  limit = 10,
  onLimitChange,
  filters,
  onFiltersChange,
}: UserTableProps) => {
  const stable = useMemo(() => columns, []);
  const [draft, setDraft] = useState<AttributionFilters>(filters);
  const { data: options } = useAcquisitionOptions();
  const applied = appliedAttributionFilters(filters);

  const apply = (next: AttributionFilters) => {
    setDraft(next);
    onFiltersChange(next);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Toolbar
        end={
          <>
            <SearchBox
              className="w-80"
              placeholder="Search name or phone"
              value={searchTerm}
              onChange={handleSearchTermChange}
            />
            <FiltersPopover
              activeCount={applied.length}
              onApply={() => onFiltersChange(draft)}
              onClearAll={() => apply({})}
            >
              <AttributionFilterSelects
                options={options}
                value={draft}
                onChange={setDraft}
              />
            </FiltersPopover>
          </>
        }
      >
        <AppliedFilters
          filters={applied}
          onClearAll={() => apply({})}
          onRemove={(id) => apply({ ...filters, [id]: undefined })}
        />
      </Toolbar>

      <DataTable
        compact
        columns={stable}
        empty={{
          title: "No users found",
          description: "Try a different search or clear the filters.",
        }}
        getRowId={(user) => user.id}
        isLoading={isLoading && items.length === 0}
        label="Users"
        pagination={{
          page,
          pageSize: limit,
          total: totalUsers,
          onPageChange: changePage,
          itemLabel: "users",
          onPageSizeChange: onLimitChange,
          pageSizes: [10, 20, 50, 100],
        }}
        rows={items}
      />
    </div>
  );
};
